import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PermissionGrantSummarySchema,
  RoleHolderSummarySchema,
  RoleSummarySchema,
  type PermissionGrantSummaryDto,
  type RoleSummaryDto,
} from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { z } from 'zod';
import { deleteJson, getData, patchJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { Facts, RecordLink } from '../finance/detail';
import { GrantDialog, grantsKey, humanRecord, ROLES } from './RolesPage';
import { useScopeNames } from './scope';
import { ScopeText } from './ScopeText';

/**
 * One role (`/admin/roles/:code`; SDD §11.4, §11.5; FRD-USR-001, USR-005, USR-008): what it is,
 * the permissions it grants — each withdrawable — and who holds it, each linking to the user.
 * Changes take effect on the next request and are audited as privileged actions. A change that
 * would leave nobody able to administer access is refused by the API and explained here.
 */
export function RoleDetailPage() {
  const { code = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const canChange = hasCapability(session.data, 'platform.role:change');
  const roles = useQuery({
    queryKey: ['platform', 'roles'],
    queryFn: () => getData(ROLES, z.array(RoleSummarySchema)),
  });
  const [editing, setEditing] = useState(false);
  const status = useMutation({
    mutationFn: (role: RoleSummaryDto) =>
      patchJson(
        `${ROLES}/${encodeURIComponent(role.code)}`,
        { status: role.status === 'active' ? 'inactive' : 'active' },
        RoleSummarySchema,
        session.data?.csrfToken ?? '',
        role.version,
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
  });
  const back = (
    <Button component={RouterLink} to="/admin/roles" startIcon={<ArrowBackIcon />}>
      All roles
    </Button>
  );
  if (roles.isPending) return <LoadingState label="Loading the role" />;
  if (roles.isError) return <ErrorState message={roles.error.message} />;
  const role = roles.data.find((r) => r.code === code);
  if (role === undefined) {
    return (
      <Stack spacing={3}>
        <PageHeader title="Role" actions={back} />
        <Section>
          <EmptyState title="No such role" detail="It does not exist, or you may not see it." />
        </Section>
      </Stack>
    );
  }
  return (
    <Stack spacing={3}>
      <PageHeader
        title={role.name}
        description={role.isPrivileged ? 'Privileged administration role' : 'Business role'}
        actions={
          <>
            {back}
            {canChange && (
              <Button
                variant="outlined"
                color={role.status === 'active' ? 'warning' : 'primary'}
                disabled={status.isPending}
                onClick={() => {
                  status.mutate(role);
                }}
              >
                {role.status === 'active' ? 'Deactivate role' : 'Activate role'}
              </Button>
            )}
            {canChange && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            )}
          </>
        }
      />
      {status.isError && <ErrorState message={status.error.message} />}
      <Section title="Role">
        <Facts
          label="Role details"
          facts={[
            ['Name', role.name],
            ['Code', role.code],
            ['Type', role.isPrivileged ? 'Privileged administration role' : 'Business role'],
            ['Status', <StatusChip key="s" status={role.status} />],
            ['Description', role.description === '' ? '—' : role.description],
          ]}
        />
        {role.status === 'inactive' && (
          <Alert severity="info" variant="outlined" sx={{ m: 2 }}>
            An inactive role grants nothing to the users who hold it, until it is activated again.
          </Alert>
        )}
      </Section>
      <Grants role={role} />
      <Holders role={role} />
      {editing && (
        <EditRoleDialog
          key={`${role.code}:${String(role.version)}`}
          role={role}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </Stack>
  );
}

/** The permissions a role grants, one row each, withdrawable with the change permission. */
function Grants({ role }: { role: RoleSummaryDto }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canView = hasCapability(session.data, 'platform.permission_grant:view');
  const canGrant = hasCapability(session.data, 'platform.permission_grant:create');
  const canWithdraw = hasCapability(session.data, 'platform.permission_grant:change');
  const [granting, setGranting] = useState(false);
  const [withdrawing, setWithdrawing] = useState<PermissionGrantSummaryDto | null>(null);
  const grants = useQuery({
    queryKey: grantsKey(role.code),
    enabled: canView,
    queryFn: () =>
      getData(
        `${ROLES}/${encodeURIComponent(role.code)}/permission-grants`,
        z.array(PermissionGrantSummarySchema),
      ),
  });
  const withdraw = useMutation({
    mutationFn: (grant: PermissionGrantSummaryDto) =>
      deleteJson(
        `${ROLES}/${encodeURIComponent(role.code)}/permission-grants/${grant.id}`,
        z.object({ id: z.string(), withdrawn: z.literal(true) }),
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      setWithdrawing(null);
      await queryClient.invalidateQueries({ queryKey: grantsKey(role.code) });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
    },
  });
  if (!canView) {
    return (
      <Section title="Permissions">
        <Alert severity="info" variant="outlined" sx={{ m: 2 }}>
          Viewing a role&apos;s permissions needs the permission-grant view permission.
        </Alert>
      </Section>
    );
  }
  return (
    <Section
      title="Permissions"
      actions={
        canGrant ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={(event) => {
              event.currentTarget.focus();
              setGranting(true);
            }}
          >
            Grant permission
          </Button>
        ) : undefined
      }
    >
      {grants.isPending && <LoadingState label="Loading permissions" />}
      {grants.isError && <ErrorState message={grants.error.message} />}
      {grants.data?.length === 0 && <EmptyState title="This role grants no permission yet" />}
      {grants.data !== undefined && grants.data.length > 0 && (
        <Table size="small" aria-label={`Permissions of ${role.name}`}>
          <TableHead>
            <TableRow>
              <TableCell>Record</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Limited to</TableCell>
              {canWithdraw && <TableCell align="right">Withdraw</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {grants.data.map((grant) => (
              <TableRow key={grant.id}>
                <TableCell sx={{ fontWeight: 600 }}>{humanRecord(grant.recordType)}</TableCell>
                <TableCell>{grant.action.replace(/_/g, ' ')}</TableCell>
                <TableCell>{limits(grant)}</TableCell>
                {canWithdraw && (
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="warning"
                      aria-label={`Withdraw ${grant.recordType}:${grant.action}`}
                      onClick={(event) => {
                        event.currentTarget.focus();
                        withdraw.reset();
                        setWithdrawing(grant);
                      }}
                    >
                      Withdraw
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <GrantDialog
        role={role}
        existing={grants.data ?? []}
        open={granting}
        onClose={() => {
          setGranting(false);
        }}
      />
      <FormDialog
        open={withdrawing !== null}
        title="Withdraw a permission"
        submitLabel="Withdraw"
        canSubmit
        submitting={withdraw.isPending}
        error={withdraw.isError ? withdraw.error.message : null}
        onClose={() => {
          setWithdrawing(null);
        }}
        onSubmit={() => {
          if (withdrawing !== null) withdraw.mutate(withdrawing);
        }}
      >
        <Typography>
          Withdraw{' '}
          <strong>
            {withdrawing === null
              ? ''
              : `${humanRecord(withdrawing.recordType)}: ${withdrawing.action.replace(/_/g, ' ')}`}
          </strong>{' '}
          {withdrawing === null
            ? ''
            : limits(withdrawing) === '—'
              ? ''
              : `(${limits(withdrawing)}) `}
          from {role.name}?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Everyone holding the role loses it on their next request. The withdrawal is audited.
        </Typography>
      </FormDialog>
    </Section>
  );
}

function limits(grant: PermissionGrantSummaryDto): string {
  const parts = [
    grant.sensitivityCategory === null ? null : grant.sensitivityCategory.replace(/_/g, ' '),
    grant.field === null ? null : `field ${grant.field}`,
    grant.ownRecordsOnly ? 'own records only' : null,
  ].filter((part): part is string => part !== null);
  return parts.length === 0 ? '—' : parts.join(', ');
}

/** Who holds the role, current and past, each linking to the user's page. */
function Holders({ role }: { role: RoleSummaryDto }) {
  const session = useSession();
  const canView = hasCapability(session.data, 'platform.role_assignment:view');
  const canSeeUsers = hasCapability(session.data, 'platform.app_user:view');
  const scope = useScopeNames();
  const holders = useQuery({
    queryKey: ['platform', 'roles', role.code, 'holders'],
    enabled: canView,
    queryFn: () =>
      getData(
        `${ROLES}/${encodeURIComponent(role.code)}/assignments`,
        z.array(RoleHolderSummarySchema),
      ),
  });
  if (!canView) return null;
  return (
    <Section title="Assigned to">
      {holders.isPending && <LoadingState label="Loading assignments" />}
      {holders.isError && <ErrorState message={holders.error.message} />}
      {holders.data?.length === 0 && <EmptyState title="Nobody holds this role" />}
      {holders.data !== undefined && holders.data.length > 0 && (
        <Table size="small" aria-label={`Holders of ${role.name}`}>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>State</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {holders.data.map((holder) => (
              <TableRow key={holder.id}>
                <TableCell>
                  <PersonName name={holder.userDisplayName} userId={holder.userId}>
                    {canSeeUsers ? (
                      <RecordLink to={`/admin/users/${holder.userId}`}>
                        {holder.userDisplayName}
                      </RecordLink>
                    ) : (
                      holder.userDisplayName
                    )}
                  </PersonName>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {holder.userEmail}
                    {holder.userStatus === 'inactive' ? ' · inactive user' : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <ScopeText scope={holder} names={scope} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{holder.effectiveFrom}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {holder.effectiveTo ?? 'Open-ended'}
                </TableCell>
                <TableCell>{holder.current ? 'Current' : 'Not current'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}

/** Name and description with If-Match; the code and privilege are fixed at creation. */
function EditRoleDialog({ role, onClose }: { role: RoleSummaryDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description);
  const changes: Record<string, string> = {};
  if (name.trim() !== role.name) changes.name = name.trim();
  if (description.trim() !== role.description) changes.description = description.trim();
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${ROLES}/${encodeURIComponent(role.code)}`,
        changes,
        RoleSummarySchema,
        session.data?.csrfToken ?? '',
        role.version,
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
    },
  });
  return (
    <FormDialog
      open
      title={`Edit role ${role.name}`}
      submitLabel="Save changes"
      canSubmit={name.trim() !== '' && Object.keys(changes).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <TextField
        label="Description"
        multiline
        minRows={2}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
      />
      <Typography variant="caption" color="text.secondary">
        The code ({role.code}) and whether the role is privileged are fixed when it is created.
      </Typography>
    </FormDialog>
  );
}
